import {
  EnumTagSemanticoDTOTipo,
  TagSemanticoDTO,
  getGetTagSemanticiQueryOptions,
  getTagSemantici,
} from '@framework/api-client';
import { Datatype, IWidgetConfigComponentProps } from '@framework/engine';
import { AsyncSelectInput, CheckboxInput, FormItem } from '@framework/form';
import { RowElement } from '@framework/ui';
import { useMemo } from 'react';
import { useWatch } from 'react-hook-form';
import { useSectionType } from '../../../schema/sections/SectionTypeContext';
import { WidgetTypeMap } from '../../widgets/map/IMapWidget';
import { WidgetUploadType } from '../../widgets/upload/IUploadWidget';
import { SemanticTagBadge } from './components/semantic-tags/SemanticTagBadge';

function dataTypeToSemanticTagType(dataType: Datatype | null | undefined) {
  switch (dataType) {
    case Datatype.Boolean:
      return EnumTagSemanticoDTOTipo.BOOLEAN;
    case Datatype.Date:
      return EnumTagSemanticoDTOTipo.DATE;
    case Datatype.Number:
      return EnumTagSemanticoDTOTipo.NUMERIC;
    case Datatype.String:
      return EnumTagSemanticoDTOTipo.TEXT;
    // Il tipo Geometria viene calcolato direttamente dal widgetType
    // e non dal dataType del widget
    default:
      return null;
  }
}

function renderSemanticTagOption(option: any) {
  return <SemanticTagBadge semanticTag={option.data.object} />;
}

export interface IMetadataDirectiveConfigComponent
  extends IWidgetConfigComponentProps {}

/**
 * Campo di input per indicare in che campo della tabella salvare il valore.
 */
export function MetadataDirectiveConfigComponent(
  props: IMetadataDirectiveConfigComponent,
) {
  const { widget, widgetType } = props;
  const isEnabled = useWatch({ name: `${props.name}.enabled` });
  const isImage = useWatch({ name: `isImage` });

  const { sectionType } = useSectionType();

  const semanticTagType = useMemo(() => {
    // Per i campi dinamici non è possibile determinare il tipo di tag semantico
    // quindi li torniamo tutti
    if (widgetType.dynamicReference) return undefined;
    if (widgetType.type === WidgetTypeMap) {
      return EnumTagSemanticoDTOTipo.GEOMETRY;
    }

    if (widgetType.type === WidgetUploadType && isImage) {
      return EnumTagSemanticoDTOTipo.IMAGE;
    }

    return dataTypeToSemanticTagType(
      widgetType.getReference?.([], widget)?.field.type,
    );
  }, [isImage, widget, widgetType]);

  if (!widgetType) return null;

  return (
    <>
      <RowElement>
        <FormItem
          name={`${props.name}.enabled`}
          label="Metadati"
          help="Indica che il valore del campo deve essere esportato come metadato nella tabella dedicata."
          style={{ marginBottom: 48 }}
        >
          <CheckboxInput name={`${props.name}.enabled`} defaultChecked={false}>
            Esporta come metadato
          </CheckboxInput>
        </FormItem>

        <FormItem
          name={`${props.name}.searchable`}
          label="Ricerca estesa"
          help="Il valore di questo campo sarà indicizzato e reso ricercabile all'interno della Ricerca Estesa testuale."
          style={{ marginBottom: 48 }}
          tooltip={
            isEnabled
              ? null
              : "Abilita l'esportazione come metadato per rendere il campo ricercabile"
          }
        >
          <CheckboxInput
            name={`${props.name}.searchable`}
            defaultChecked={false}
          >
            Includi nella ricerca estesa
          </CheckboxInput>
        </FormItem>
      </RowElement>
      <RowElement>
        <FormItem
          name={`${props.name}.semanticTag`}
          label="Tag semantico"
          help="Indica il tag semantico da associare al campo."
          tooltip={
            isEnabled
              ? null
              : "Abilita l'esportazione come metadato per assegnare un tag semantico al campo"
          }
        >
          <AsyncSelectInput
            allowClear
            name={`${props.name}.semanticTag`}
            queryFn={getTagSemantici}
            initialParams={{
              ordinamenti: ['nome'],
              tipo: semanticTagType ?? undefined,
              // Permettiamo di esportare:
              // - i tag di FRONTOFFICE sia su FO (Richieste) che su BO (Attività)
              // - i tag di BACKOFFICE solo su BO (Attività)
              tipoSezione:
                sectionType === 'BACKOFFICE' ? undefined : sectionType,
            }}
            queryOptions={getGetTagSemanticiQueryOptions}
            searchByField="nome"
            asOption={(semanticTag: TagSemanticoDTO) => ({
              value: semanticTag.id!,
              label: semanticTag.titolo ?? semanticTag.nome ?? semanticTag.id!,
            })}
            placeholder="Tag semantico"
            optionRender={renderSemanticTagOption}
            filter={option => {
              // Se li sto ritornando tutti, rimuovo comunque i tag di tipo
              // geometry che sono dedicati ai widget mappa
              if (semanticTagType == null) {
                return option.tipo !== EnumTagSemanticoDTOTipo.GEOMETRY;
              }
              if (semanticTagType === EnumTagSemanticoDTOTipo.IMAGE) {
                return option.tipo === EnumTagSemanticoDTOTipo.IMAGE;
              }
              return true;
            }}
          />
        </FormItem>
      </RowElement>
    </>
  );
}
